/**
 * NwslPlayoffStandings — iter87.362. Round-by-round bracket view for
 * NWSL Playoffs. Sibling of FootballPlayoffStandings but fixtures are
 * flat (not nested by round on the server) — grouped client-side by
 * `fixture.round` in fixed QF → SF → Final order. Served by
 * `GET /nwsl/tournaments/{id}/bracket`.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";

const ROUND_ORDER = ["qf", "sf", "final"];
const RESOLUTION_LABEL = { regulation: "FT", extra_time: "AET", penalties: "PKs" };

export default function NwslPlayoffStandings({ tournamentId }) {
    const [bracket, setBracket] = useState(null);

    useEffect(() => {
        if (!tournamentId) return;
        api.getNwslBracket(tournamentId).then(setBracket).catch(() => setBracket(null));
    }, [tournamentId]);

    if (!bracket || !(bracket.fixtures || []).length) {
        return (
            <div className="brutal-card p-4 sm:p-5 text-center text-zinc-500 text-xs uppercase tracking-widest" data-testid="nwsl-standings-empty">
                Playoff bracket not synced yet
            </div>
        );
    }

    const teamsById = {};
    for (const tm of bracket.teams || []) teamsById[tm.id] = tm;
    const nameFor = (id) => teamsById[id]?.name || "TBD";
    const seedFor = (id) => teamsById[id]?.seed;

    const roundLabels = bracket.round_labels || {};
    const rounds = ROUND_ORDER
        .map((key) => ({ key, label: roundLabels[key] || key, fixtures: (bracket.fixtures || []).filter((f) => f.round === key) }))
        .filter((r) => r.fixtures.length > 0);

    const TeamRow = ({ id, isWinner, isLoser, score }) => (
        <div className={`flex items-center justify-between px-2 py-2 rounded-lg border transition-colors ${isWinner ? "bg-cyber/10 border-cyber/30" : "border-transparent"}`}>
            <div className="flex items-center min-w-0 gap-2">
                <TeamSwatch name={nameFor(id)} sport="nwsl" size="w-6 h-6 text-[8px]" />
                <span className="inline-flex w-5 h-5 items-center justify-center rounded-full bg-white/[0.04] border border-white/[0.08] font-mono text-[9px] font-bold text-zinc-400 shrink-0">
                    {seedFor(id) ?? "\u2013"}
                </span>
                <span className={`text-sm font-semibold truncate flex-1 ${isWinner ? "text-cyber" : isLoser ? "text-zinc-500 line-through" : "text-zinc-200"}`}>
                    {nameFor(id)}
                </span>
            </div>
            <span className={`font-mono font-black text-sm tabular-nums ml-2 ${isWinner ? "text-white" : "text-zinc-500"}`}>{score ?? "\u2013"}</span>
        </div>
    );

    return (
        <div className="space-y-5" data-testid="nwsl-playoff-standings">
            {rounds.map((r) => (
                <div key={r.key} data-testid={`nwsl-standings-round-${r.key}`}>
                    <h4 className="font-display font-extrabold uppercase text-base sm:text-lg tracking-tight mb-3 text-white border-b border-white/[0.08] pb-2">
                        {r.label}
                    </h4>
                    <div className="space-y-3">
                        {r.fixtures.map((f) => (
                            <div key={f.id} data-testid={`nwsl-standings-fixture-${f.id}`} className="brutal-card-light border border-white/[0.08] p-3 rounded-lg flex flex-col gap-1.5">
                                {f.resolution && (
                                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold self-end" data-testid={`nwsl-standings-fixture-${f.id}-resolution`}>
                                        {RESOLUTION_LABEL[f.resolution] || f.resolution}
                                    </span>
                                )}
                                <TeamRow id={f.team_a_id} isWinner={f.played && f.winner_team_id === f.team_a_id} isLoser={f.played && f.winner_team_id !== f.team_a_id} score={f.score_a} />
                                <TeamRow id={f.team_b_id} isWinner={f.played && f.winner_team_id === f.team_b_id} isLoser={f.played && f.winner_team_id !== f.team_b_id} score={f.score_b} />
                                {f.played && (
                                    <p data-testid={`nwsl-standings-fixture-${f.id}-summary`} className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mt-1 pt-2 border-t border-white/[0.04]">
                                        {nameFor(f.winner_team_id)} wins
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
